import { useEffect, useMemo, useRef, useState } from "react";
import { createCollection, bulkCreateSchemas, bulkCreateTemplates } from "../api/actions";
import { getCollection, getSchema } from "../api/atomicApi";
import { logActivity } from "../api/activityLog";
import { toStringAttributeMap, toAttributeMap } from "../utils/attributes";
import { uploadImageToIpfs } from "../utils/ipfs";
import { useWallet } from "../wallet/WalletContext";
import { austinsFinestRoster } from "../data/austinsFinestRoster";
import type { Roster, RosterItem } from "../data/rosterTypes";
import { showSuccess, showError } from "../components/Dialog";

// 20 hit a real "billed CPU time > max billable CPU time" wallet error on a
// freshly-created account with modest staked CPU — createtempl's
// immutable_data payload is heavy enough that 20 in one transaction can
// exceed a light account's per-tx CPU ceiling. 10 halves that per-transaction
// cost (more signing popups, but each one actually fits).
const TEMPLATE_BATCH_SIZE = 10;

type ArtDecision = "include" | "skip";

/**
 * Bulk launcher for a whole collection at once — collection, every schema,
 * and every template from a roster (see src/data/rosterTypes.ts) — instead
 * of the one-at-a-time Advanced Create form. Built for Austin's Finest's
 * 54-item roster (one button loads it below), but the roster shape isn't
 * game-specific: "Upload a roster JSON instead" takes any file matching
 * Roster from rosterTypes.ts, so this is reusable for the next
 * many-template launch too.
 */
export function BulkCreate() {
  const { session, connect, connecting } = useWallet();
  const [roster, setRoster] = useState<Roster | null>(null);
  const [rosterError, setRosterError] = useState<string | null>(null);
  const [imageFiles, setImageFiles] = useState<Map<string, File>>(new Map());
  const [uploadedHashes, setUploadedHashes] = useState<Map<string, string>>(new Map());
  const [artDecisions, setArtDecisions] = useState<Map<string, ArtDecision>>(new Map());
  const [royaltyOverrides, setRoyaltyOverrides] = useState<Map<string, number>>(new Map());
  const [busy, setBusy] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [collectionDone, setCollectionDone] = useState(false);
  const [schemasDone, setSchemasDone] = useState(false);
  const [createdKeys, setCreatedKeys] = useState<Set<string>>(new Set());
  const [checkingOnChain, setCheckingOnChain] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const jsonInputRef = useRef<HTMLInputElement>(null);

  // A page refresh (or just reopening this tab) resets collectionDone/
  // schemasDone to false even when the collection/schemas were already
  // created in an earlier visit — the buttons below only track "did I see
  // this succeed in THIS page load," not on-chain reality. Without this,
  // re-clicking "Create collection" after a refresh would resubmit
  // `createcol` for a collection that already exists and fail with
  // "A collection with this name already exists," permanently soft-locking
  // steps 2/3 since collectionDone never flips true on that error path.
  // So whenever a roster loads, check what's actually on-chain first and
  // skip straight past whatever's already done.
  useEffect(() => {
    if (!roster) return;
    let cancelled = false;
    setCheckingOnChain(true);
    (async () => {
      const existingCollection = await getCollection(roster.collectionName);
      if (cancelled) return;
      if (existingCollection) setCollectionDone(true);

      // Schemas are created together in one transaction, so treat them as
      // done only once every schema in the roster already exists — a
      // partial set (e.g. from a batch that reverted midway) still needs
      // the create-schemas step to fill in whatever's missing. (createschema
      // isn't itself batchable per-missing-schema here — bulkCreateSchemas
      // always submits the full list — so a true partial state would need
      // manual cleanup, but that's not a state normal use of this page can
      // produce today.)
      const schemaChecks = await Promise.all(
        roster.schemas.map((s) => getSchema(roster.collectionName, s.schemaName))
      );
      if (cancelled) return;
      if (schemaChecks.every((s) => s !== null)) setSchemasDone(true);
      setCheckingOnChain(false);
    })().catch(() => {
      if (!cancelled) setCheckingOnChain(false);
    });
    return () => {
      cancelled = true;
    };
  }, [roster]);

  const itemKey = (item: RosterItem) => `${item.schemaName}:${item.templateKey}`;

  const missingArtItems = useMemo(
    () => (roster ? roster.items.filter((item) => item.imageFilename && !imageFiles.has(item.imageFilename.toLowerCase())) : []),
    [roster, imageFiles]
  );
  const noArtPlannedItems = useMemo(
    () => (roster ? roster.items.filter((item) => !item.imageFilename) : []),
    [roster]
  );
  const logoMatched = roster?.logoFilename ? imageFiles.has(roster.logoFilename.toLowerCase()) : false;

  async function ensureSession() {
    if (session) return session;
    await connect();
    return undefined;
  }

  function handleFilesSelected(files: FileList | null) {
    if (!files) return;
    setImageFiles((prev) => {
      const next = new Map(prev);
      for (const file of Array.from(files)) {
        next.set(file.name.toLowerCase(), file);
      }
      return next;
    });
  }

  function handleRosterJson(file: File | undefined | null) {
    if (!file) return;
    setRosterError(null);
    file
      .text()
      .then((text) => {
        const parsed = JSON.parse(text) as Roster;
        if (!parsed.collectionName || !Array.isArray(parsed.items) || !Array.isArray(parsed.schemas)) {
          throw new Error("Doesn't look like a roster file (missing collectionName/schemas/items).");
        }
        setRoster(parsed);
      })
      .catch((err) => setRosterError(err instanceof Error ? err.message : "Couldn't read that file"));
  }

  function artDecisionFor(item: RosterItem): ArtDecision {
    // Only auto-include when the roster's expected filename is actually
    // matched to a selected file — not just because the roster *names* one.
    // A roster item whose art didn't match (wrong filename, missing file)
    // must go through the same explicit opt-in as an item with no art
    // planned at all; otherwise it'd silently get created with no image,
    // permanently, without the creator ever deciding that.
    if (item.imageFilename && imageFiles.has(item.imageFilename.toLowerCase())) return "include";
    return artDecisions.get(itemKey(item)) ?? "skip";
  }

  async function handleCreateCollection() {
    if (!roster) return;
    const s = await ensureSession();
    if (!s) return;
    setBusy("collection");
    try {
      let logoHash = "";
      if (roster.logoFilename) {
        const file = imageFiles.get(roster.logoFilename.toLowerCase());
        if (file) {
          setProgress("Uploading collection logo to IPFS…");
          logoHash = await uploadImageToIpfs(file);
        }
      }
      await createCollection(s, {
        collectionName: roster.collectionName,
        allowNotify: true,
        authorizedAccounts: [s.actor.toString()],
        notifyAccounts: [],
        marketFee: roster.marketFee,
        data: toStringAttributeMap({ name: roster.displayName, description: roster.description, img: logoHash }),
      });
      setCollectionDone(true);
      logActivity({
        type: "collection_created",
        account: s.actor.toString(),
        collectionName: roster.collectionName,
        note: roster.displayName,
      });
    } catch (err) {
      showError(err, "Failed to create collection");
    } finally {
      setProgress(null);
      setBusy(null);
    }
  }

  async function handleCreateSchemas() {
    if (!roster) return;
    const s = await ensureSession();
    if (!s) return;
    setBusy("schemas");
    try {
      await bulkCreateSchemas(
        s,
        roster.schemas.map((schema) => ({
          collectionName: roster.collectionName,
          schemaName: schema.schemaName,
          format: [
            { name: "name", type: "string" },
            { name: "img", type: "image" },
            { name: "description", type: "string" },
            ...schema.extraFields,
          ],
        }))
      );
      setSchemasDone(true);
      logActivity({
        type: "schemas_created",
        account: s.actor.toString(),
        collectionName: roster.collectionName,
        note: `${roster.schemas.length} schema${roster.schemas.length === 1 ? "" : "s"}`,
      });
    } catch (err) {
      showError(err, "Failed to create schemas");
    } finally {
      setBusy(null);
    }
  }

  async function handleCreateTemplates() {
    if (!roster) return;
    const s = await ensureSession();
    if (!s) return;

    // Excludes anything already created on a prior run of this button — a
    // batch partway through can fail (network hiccup, a rejected wallet
    // popup) after earlier batches already landed on-chain; without this,
    // clicking the button again to finish the rest would resubmit those
    // already-succeeded templates too, creating duplicates.
    const itemsToCreate = roster.items.filter(
      (item) => artDecisionFor(item) === "include" && !createdKeys.has(itemKey(item))
    );
    if (itemsToCreate.length === 0) return;
    setBusy("templates");
    try {
      // Upload any not-yet-uploaded matched art first, so every template's
      // immutable_data has its real hash instead of minting blank and
      // being stuck that way forever (see bulkCreateTemplates' immutability note).
      const hashes = new Map(uploadedHashes);
      const withArt = itemsToCreate.filter((i) => i.imageFilename);
      for (let i = 0; i < withArt.length; i++) {
        const item = withArt[i];
        const filename = item.imageFilename!.toLowerCase();
        if (hashes.has(filename)) continue;
        const file = imageFiles.get(filename);
        if (!file) continue;
        setProgress(`Uploading art ${i + 1}/${withArt.length} (${item.imageFilename})…`);
        const hash = await uploadImageToIpfs(file);
        hashes.set(filename, hash);
      }
      setUploadedHashes(hashes);

      let created = 0;
      for (let start = 0; start < itemsToCreate.length; start += TEMPLATE_BATCH_SIZE) {
        const batch = itemsToCreate.slice(start, start + TEMPLATE_BATCH_SIZE);
        setProgress(`Creating templates ${start + 1}–${start + batch.length} of ${itemsToCreate.length}…`);
        await bulkCreateTemplates(
          s,
          batch.map((item) => {
            const imgHash = item.imageFilename ? hashes.get(item.imageFilename.toLowerCase()) : undefined;
            const overrideKey = itemKey(item);
            const extraValues = { ...item.extraValues };
            if (royaltyOverrides.has(overrideKey)) {
              extraValues.royaltybps = royaltyOverrides.get(overrideKey)!;
            }
            const extraTyped: Record<string, { type: string; value: string | number | boolean }> = {};
            const schema = roster.schemas.find((sc) => sc.schemaName === item.schemaName);
            for (const field of schema?.extraFields ?? []) {
              if (field.name in extraValues) {
                extraTyped[field.name] = { type: field.type, value: extraValues[field.name] };
              }
            }
            return {
              collectionName: roster.collectionName,
              schemaName: item.schemaName,
              transferable: item.transferable,
              burnable: item.burnable,
              maxSupply: item.maxSupply,
              immutableData: toAttributeMap({
                name: { type: "string", value: item.displayName },
                // Note: "image" is a valid *schema_format* type (a display
                // hint AtomicHub/explorers use to know to render this field
                // as a picture) but NOT a valid immutable_data attribute
                // variant — actual attribute values only support the fixed
                // primitive set (string, intN/uintN, floatN, and the *_VEC
                // forms). An image is still just a string (its IPFS hash)
                // at the immutable_data level.
                img: imgHash ? { type: "string", value: imgHash } : undefined,
                description: { type: "string", value: item.description },
                ...extraTyped,
              }),
            };
          })
        );
        created += batch.length;
        setCreatedKeys((prev) => {
          const next = new Set(prev);
          for (const item of batch) next.add(itemKey(item));
          return next;
        });
      }
      showSuccess(`Created ${created} template${created === 1 ? "" : "s"}. Check AtomicHub for their template_ids.`);
      if (created > 0) {
        logActivity({
          type: "templates_created",
          account: s.actor.toString(),
          collectionName: roster.collectionName,
          note: `${created} template${created === 1 ? "" : "s"}`,
        });
      }
    } catch (err) {
      showError(err, "Failed to create templates");
    } finally {
      setProgress(null);
      setBusy(null);
    }
  }

  function setRoyalty(item: RosterItem, value: number) {
    setRoyaltyOverrides((prev) => {
      const next = new Map(prev);
      next.set(itemKey(item), value);
      return next;
    });
  }

  function toggleArtDecision(item: RosterItem) {
    setArtDecisions((prev) => {
      const next = new Map(prev);
      next.set(itemKey(item), artDecisionFor(item) === "include" ? "skip" : "include");
      return next;
    });
  }

  const businessOwnerItems = roster?.items.filter((i) => i.schemaName === "bizowner") ?? [];
  const includedCount = roster?.items.filter((item) => artDecisionFor(item) === "include").length ?? 0;
  const remainingCount = roster
    ? roster.items.filter((item) => artDecisionFor(item) === "include" && !createdKeys.has(itemKey(item))).length
    : 0;

  return (
    <div className="page page--narrow">
      <div className="page__header">
        <h1>Bulk Create</h1>
        <p className="page__subtitle">
          Launch a whole collection — every schema and every template — from a roster, instead of clicking through{" "}
          <a href="/create-advanced">Advanced Create</a>'s one-at-a-time forms dozens of times. For a single custom
          NFT, use <a href="/create">Create an NFT</a> instead.
        </p>
      </div>

      {!roster && (
        <div className="mint-form">
          <h2>1. Load a roster</h2>
          <button className="btn btn--primary" type="button" onClick={() => setRoster(austinsFinestRoster)}>
            Load Austin's Finest roster (54 items)
          </button>
          <p className="page__subtitle" style={{ marginTop: 12 }}>
            Or upload a roster JSON file matching the Roster shape in <code>src/data/rosterTypes.ts</code>:
          </p>
          <input
            ref={jsonInputRef}
            type="file"
            accept="application/json"
            onChange={(e) => handleRosterJson(e.target.files?.[0])}
          />
          {rosterError && <p className="state-message state-message--error">{rosterError}</p>}
        </div>
      )}

      {roster && (
        <>
          <div className="mint-form">
            <h2>1. Roster loaded — {roster.displayName}</h2>
            <p>
              <strong>{roster.collectionName}</strong> · {roster.schemas.length} schemas · {roster.items.length} templates
            </p>
            <p className="page__subtitle">{roster.description}</p>
            <button className="link-button" type="button" onClick={() => setRoster(null)}>
              Load a different roster
            </button>
          </div>

          <div className="mint-form">
            <h2>2. Match artwork</h2>
            <p className="page__subtitle">
              Select every image file at once (logo + all delivered card art) — matched by filename against the
              roster. Files are only uploaded to IPFS when you actually create their template/collection, not here.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleFilesSelected(e.target.files)}
            />
            <p style={{ marginTop: 12 }}>
              {imageFiles.size} file{imageFiles.size === 1 ? "" : "s"} selected · logo{" "}
              {logoMatched ? "matched ✓" : "not matched"} ·{" "}
              {roster.items.filter((i) => i.imageFilename).length - missingArtItems.length}/
              {roster.items.filter((i) => i.imageFilename).length} expected item images matched
            </p>

            {(missingArtItems.length > 0 || noArtPlannedItems.length > 0) && (
              <div className="state-message" style={{ marginTop: 12 }}>
                <strong>{missingArtItems.length + noArtPlannedItems.length} templates have no art</strong> —
                AtomicAssets can never change a template's image once it's created (immutable_data is permanent),
                so anything created now with no image stays that way forever; the only fix later is a brand-new
                template with the real art. Decide per item below whether to create it now anyway or hold it back.
              </div>
            )}
          </div>

          {businessOwnerItems.length > 0 && (
            <div className="mint-form">
              <h2>3. businessowner royalties (placeholder — edit before signing)</h2>
              <p className="page__subtitle">
                royaltybps defaults to 500 (5%) for every license, matching the collection's marketFee — override
                any shop below if you want a different rate before signing.
              </p>
              <div style={{ overflowX: "auto" }}>
                <table className="bulk-table">
                  <thead>
                    <tr>
                      <th>Shop</th>
                      <th>royaltybps</th>
                      <th>Art</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businessOwnerItems.map((item) => (
                      <tr key={itemKey(item)}>
                        <td>{item.displayName}</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max="10000"
                            style={{ width: 90 }}
                            value={royaltyOverrides.get(itemKey(item)) ?? (item.extraValues.royaltybps as number)}
                            onChange={(e) => setRoyalty(item, Number(e.target.value))}
                          />
                        </td>
                        <td>
                          {item.imageFilename && imageFiles.has(item.imageFilename.toLowerCase()) ? (
                            // Art is already matched to a selected file — this item will be
                            // created with that real image no matter what. There's nothing to
                            // decide here, so show a plain status instead of a checkbox that
                            // looks interactive but can't actually be unchecked (artDecisionFor
                            // always returns "include" once a file is matched).
                            <span className="status-ok">Art matched ✓</span>
                          ) : (
                            <label>
                              <input
                                type="checkbox"
                                checked={artDecisionFor(item) === "include"}
                                onChange={() => toggleArtDecision(item)}
                              />{" "}
                              create without art
                            </label>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {[...missingArtItems, ...noArtPlannedItems.filter((i) => i.schemaName !== "bizowner")].length > 0 && (
            <div className="mint-form">
              <h2>{businessOwnerItems.length > 0 ? "4" : "3"}. Other items missing art</h2>
              <div style={{ overflowX: "auto" }}>
                <table className="bulk-table">
                  <thead>
                    <tr>
                      <th>Schema</th>
                      <th>Item</th>
                      <th>Create anyway?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...missingArtItems, ...noArtPlannedItems.filter((i) => i.schemaName !== "bizowner")].map(
                      (item) => (
                        <tr key={itemKey(item)}>
                          <td>{item.schemaName}</td>
                          <td>{item.displayName}</td>
                          <td>
                            <label>
                              <input
                                type="checkbox"
                                checked={artDecisionFor(item) === "include"}
                                onChange={() => toggleArtDecision(item)}
                              />{" "}
                              include without art
                            </label>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mint-form">
            <h2>{businessOwnerItems.length > 0 ? "5" : "4"}. Run it</h2>
            <p className="page__subtitle">
              Three signed transactions in sequence: the collection, all {roster.schemas.length} schemas together,
              then templates in batches of {TEMPLATE_BATCH_SIZE} ({includedCount} template{includedCount === 1 ? "" : "s"}{" "}
              selected to create ={" "}
              {Math.ceil(includedCount / TEMPLATE_BATCH_SIZE) || 0} batch
              {Math.ceil(includedCount / TEMPLATE_BATCH_SIZE) === 1 ? "" : "es"}).
            </p>
            {checkingOnChain && (
              <p className="page__subtitle">Checking what's already on-chain for {roster.collectionName}…</p>
            )}
            <div className="mint-form__amount-row">
              <button
                className="btn btn--primary"
                type="button"
                onClick={handleCreateCollection}
                disabled={busy !== null || collectionDone || connecting || checkingOnChain}
              >
                {collectionDone ? "Collection created ✓" : busy === "collection" ? progress ?? "Creating…" : "1. Create collection"}
              </button>
              <button
                className="btn btn--primary"
                type="button"
                onClick={handleCreateSchemas}
                disabled={busy !== null || !collectionDone || schemasDone || checkingOnChain}
              >
                {schemasDone ? "Schemas created ✓" : busy === "schemas" ? "Creating…" : "2. Create schemas"}
              </button>
              <button
                className="btn btn--primary"
                type="button"
                onClick={handleCreateTemplates}
                disabled={busy !== null || !schemasDone || remainingCount === 0 || checkingOnChain}
              >
                {busy === "templates"
                  ? progress ?? "Creating…"
                  : createdKeys.size === 0
                    ? `3. Create ${includedCount} templates`
                    : remainingCount === 0
                      ? `All ${includedCount} templates created ✓`
                      : `${createdKeys.size}/${includedCount} created — create remaining ${remainingCount}`}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
